import React, { useState, useEffect } from 'react';
import { services } from '../../../services/api';
import { PageHeader, Tabs, Loader } from '../../../components/ds';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { FiUserCheck, FiUsers, FiShield, FiLayers, FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import RoleDefine from './RoleDefine';
import TierManagement from './TierManagement';


//--> Provider glyph + label. Shared by the desktop table and the phone cards so
//    the two presentations can't drift.
const ProviderBadge = ({ user }) => (
  <div className="flex items-center gap-2">
    {user.auth_provider === 'google' ? (
      /* Inline SVG, not a hotlinked Wikipedia file: that was an external network
         dependency on every row render, which silently fails offline / behind a
         strict CSP and leaves an empty white circle. */
      <div className="w-5 h-5 flex items-center justify-center bg-white rounded-full p-1 shadow-sm shrink-0">
        <svg viewBox="0 0 48 48" className="w-full h-full" role="img" aria-label="Google">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
      </div>
    ) : (
      <div className="w-6 h-6 flex items-center justify-center bg-bg-3 rounded-full p-1 shrink-0">
        <FiShield className="w-3 h-3 text-fg-2" />
      </div>
    )}
    <span className="capitalize">{user.auth_provider || 'Email'}</span>
  </div>
);

const UserManagement = () => {
  const { showSnackbar } = useSnackbar();
  //! Phone gets CARDS, not the table. A 5-column table only "fits" a 375px
  //! screen by scrolling sideways, which reads as broken/cut-off and makes the
  //! Role/Tier selects — the point of this page — something you pan around a
  //! spreadsheet to reach. Reachable is not usable. Desktop keeps the table.
  const { isPhone } = useBreakpoint();
  const [activeTab, setActiveTab] = useState('customers');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create Internal User State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'viewer', first_name: '', last_name: '' });
  const [createLoading, setCreateLoading] = useState(false);

  // Role Update State
  const [updatingUserId, setUpdatingUserId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await services.users.listUsers(0, 500);
      setUsers(response.data.users || response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const internalUsers = users.filter(u => u.auth_provider === 'internal-admin');
  const customerUsers = users.filter(u => u.auth_provider !== 'internal-admin');
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
      try {
          const response = await services.tiers.list();
          // Backend returns { tiers: [...] }
          setTiers(response.data?.tiers || response.data || []);
      } catch (error) {
          console.error("Failed to load tiers", error);
          setTiers([]);
      }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await services.users.createInternalUser(newUser);
      showSnackbar('Internal user created successfully', 'success');
      setShowCreateModal(false);
      setNewUser({ email: '', password: '', role: 'viewer', first_name: '', last_name: '' });
      fetchUsers();
    } catch (error) {
       console.error('Error creating user:', error);
       showSnackbar('Failed to create user: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRoleUpdate = async (userId, newRole) => {
    setUpdatingUserId(userId);
    try {
      await services.users.updateUserRole(userId, newRole);
      showSnackbar('Role updated successfully', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Updates role error:', error);
      showSnackbar('Failed to update role', 'error');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleTierUpdate = async (userId, newTier) => {
      setUpdatingUserId(userId);
      try {
        await services.users.updateUserTier(userId, newTier);
        showSnackbar('Tier updated successfully', 'success');
        fetchUsers();
      } catch (error) {
        console.error('Updates tier error:', error);
        showSnackbar('Failed to update tier', 'error');
      } finally {
        setUpdatingUserId(null);
      }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role?.toLowerCase()) {
      case 'superuser': return 'loss';
      case 'admin': return 'role';
      case 'trader': return 'info';
      default: return 'neutral';
    }
  };

  //--> Shared empty state for both presentations.
  const emptyState = (
    <div className="p-10 text-center text-fg-2">
      <div className="mx-auto w-16 h-16 bg-bg-3 rounded-full flex items-center justify-center mb-4">
        <FiUsers size={24} />
      </div>
      <p className="text-lg font-medium">No users found</p>
      <p className="text-sm mt-1">Try adjusting your filters or create a new user.</p>
    </div>
  );

  //--> PHONE: one card per user. Everything is visible without panning, and the
  //    Role/Tier selects are full-width thumb targets instead of table cells.
  const renderUserCards = (userList, isInternal = false) => (
    <div className="flex flex-col gap-3">
      {userList.map((user) => (
        <div key={user.id} className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-full bg-accent flex items-center justify-center text-white font-bold text-lg shadow-sm">
              {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-fg-1">
                {user.first_name} {user.last_name}
              </div>
              <div className="truncate text-sm text-fg-2">{user.email}</div>
            </div>
            <span className={`ds-badge ${user.is_active ? 'profit' : 'loss'} sm shrink-0`}>
              <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-[var(--badge-profit-dot)]' : 'bg-[var(--badge-loss-dot)]'}`}></span>
              {user.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="mt-3 text-sm text-fg-2">
            <ProviderBadge user={user} />
          </div>

          <div className="mt-3 flex flex-col gap-3 border-t border-[var(--divider)] pt-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-fg-3">Role</span>
              {isInternal ? (
                <select
                  value={user.role}
                  onChange={(e) => handleRoleUpdate(user.id, e.target.value)}
                  disabled={updatingUserId === user.id}
                  className="ds-input block w-full"
                >
                  <option value="viewer">Viewer</option>
                  <option value="trader">Trader</option>
                  <option value="admin">Admin</option>
                  <option value="superuser">Superuser</option>
                </select>
              ) : (
                <span className={`ds-badge ${getRoleBadgeVariant(user.role)} sm`}>{user.role}</span>
              )}
            </label>

            {!isInternal && (
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-fg-3">Tier</span>
                <select
                  value={user.subscription_plan || 'free'}
                  onChange={(e) => handleTierUpdate(user.id, e.target.value)}
                  disabled={updatingUserId === user.id}
                  className="ds-input block w-full"
                >
                  <option value="free">Free</option>
                  {tiers.map((t) => (
                    <option key={t.id} value={t.tier_name}>
                      {t.display_name || t.tier_name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      ))}
      {userList.length === 0 && (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2">{emptyState}</div>
      )}
    </div>
  );

  const renderUserTable = (userList, isInternal = false) => (
    // overflow-x-auto, not overflow-hidden: this table is min-w-full with nowrap
    // cells and px-6, so its min-content is way past a phone. Hidden CLIPPED it —
    // on a 375px screen the Role and Tier selects (the point of this page) were
    // unreachable with no scrollbar and no gesture to recover them.
    <div className="overflow-x-auto bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-bg-3/50 dark:bg-bg-2/50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">User</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">Provider</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">Role</th>
            {!isInternal && <th className="px-6 py-4 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">Tier</th>}
            <th className="px-6 py-4 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {userList.map((user) => (
            <tr key={user.id} className="hover:bg-bg-3/80 dark:hover:bg-bg-3/30 transition-colors duration-150">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center text-white font-bold text-lg shadow-sm">
                        {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                    </div>
                    <div className="ml-4">
                        <div className="text-sm font-medium text-fg-1">{user.first_name} {user.last_name}</div>
                        <div className="text-sm text-fg-2">{user.email}</div>
                    </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                <ProviderBadge user={user} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                {isInternal ? (
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleUpdate(user.id, e.target.value)}
                    disabled={updatingUserId === user.id}
                    className="ds-input block w-full"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="trader">Trader</option>
                    <option value="admin">Admin</option>
                    <option value="superuser">Superuser</option>
                  </select>
                ) : (
                  <span className={`ds-badge ${getRoleBadgeVariant(user.role)} sm`}>
                    {user.role}
                  </span>
                )}
              </td>
              {!isInternal && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                      <select
                        value={user.subscription_plan || 'free'}
                        onChange={(e) => handleTierUpdate(user.id, e.target.value)}
                        disabled={updatingUserId === user.id}
                        className="ds-input block w-full"
                      >
                         <option value="free">Free</option>
                          {tiers.map(t => (
                              <option key={t.id} value={t.tier_name}>{t.display_name || t.tier_name}</option>
                          ))}
                      </select>
                  </td>
              )}
               <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                <span className={`ds-badge ${user.is_active ? 'profit' : 'loss'} sm`}>
                   <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-[var(--badge-profit-dot)]' : 'bg-[var(--badge-loss-dot)]'}`}></span>
                   {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {userList.length === 0 && emptyState}
    </div>
  );

  //--> Cards on a phone, table on desktop.
  const renderUsers = (userList, isInternal = false) =>
    isPhone ? renderUserCards(userList, isInternal) : renderUserTable(userList, isInternal);

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10">
      <div>
        {/* Desktop only — on phone the app shell already renders "User Management",
            so showing this too gave the page two stacked headers. */}
        <div className="hidden lg:block">
          <PageHeader title="User Management" subtitle="Manage users, roles, and access permissions." />
        </div>

            {/* Tier-2: flat tab row (bordered-bottom) — no card enclosure */}
            <div className="border-b border-[var(--divider)]">
                <Tabs
                    value={activeTab}
                    onChange={setActiveTab}
                    tabs={[
                        { label: 'Customers', value: 'customers', icon: <FiUsers size={18} />, badge: customerUsers.length },
                        { label: 'Internal Users', value: 'internal', icon: <FiShield size={18} />, badge: internalUsers.length },
                        { label: 'Role Definitions', value: 'roles', icon: <FiUserCheck size={18} /> },
                        { label: 'Subscription Tiers', value: 'tiers', icon: <FiLayers size={18} />, badge: tiers.length },
                    ]}
                />
            </div>

            {/* Content — flows directly on the page (no interior fill) */}
            <div className="pt-6 min-h-[600px]">
                {activeTab === 'customers' && (
                <div className="animate-fade-in">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                             <h3 className="ds-h3 text-fg-1">Customer Accounts</h3>
                             <p className="text-sm text-fg-2 mt-1">Manage registered customers and view their subscription status.</p>
                        </div>
                        <div className="flex gap-3">
                             {/* Add Search/Filter components here if needed */}
                        </div>
                    </div>
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <Loader label="Loading customers…" />
                        </div>
                    ) : renderUsers(customerUsers, false)}
                </div>
                )}

                {activeTab === 'internal' && (
                <div className="animate-fade-in">
                    {/* Stack on phone: the heading + its long subtitle and the
                        Create button squeezed each other at 375px. */}
                    <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                             <h3 className="ds-h3 text-fg-1">Internal Team</h3>
                             <p className="text-sm text-fg-2 mt-1">Manage administrators, support staff, and their access levels.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(true)}
                            className="btn btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                        >
                            <FiPlus className="w-4 h-4" /> Create Internal User
                        </button>
                    </div>
                    {loading ? (
                         <div className="flex justify-center items-center h-64">
                            <Loader label="Loading users…" />
                        </div>
                    ) : renderUsers(internalUsers, true)}
                </div>
                )}

                {activeTab === 'roles' && <div className="animate-fade-in"><RoleDefine /></div>}
                
                {activeTab === 'tiers' && <div className="animate-fade-in"><TierManagement /></div>}
            </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          {/* max-h + overflow-y-auto: on a phone this modal had no vertical
              escape — a tall form simply ran off screen. Matches the pattern
              already used by RoleDefine / TierManagement. */}
          <div className="bg-bg-2 rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto transform transition-all scale-100 border border-[var(--divider)]">
            <h3 className="ds-h3 mb-6 text-fg-1 flex items-center gap-2">
                <div className="p-2 bg-[color-mix(in_oklch,var(--theme-purple)_14%,transparent)] rounded-lg text-[var(--theme-purple)]">
                    <FiShield size={20} />
                </div>
                New Internal User
            </h3>
            
            <form onSubmit={handleCreateUser} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-fg-2 mb-1.5">First Name</label>
                  <input
                    type="text"
                    className="ds-input w-full"
                    value={newUser.first_name}
                    onChange={e => setNewUser({...newUser, first_name: e.target.value})}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-fg-2 mb-1.5">Last Name</label>
                  <input
                    type="text"
                    className="ds-input w-full"
                    value={newUser.last_name}
                    onChange={e => setNewUser({...newUser, last_name: e.target.value})}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-fg-2 mb-1.5">Email Address</label>
                <input 
                  type="email"
                  required
                  className="ds-input w-full"
                  value={newUser.email}
                  onChange={e => setNewUser({...newUser, email: e.target.value})}
                  placeholder="john@company.com"
                />
              </div>

              <div className="hidden"> 
                {/* Auto-generated on backend for internal users usually, but kept for simplicity if needed manually */}
                <label className="block text-sm font-medium dark:text-fg-3">Password</label>
                <input
                  type="password"
                  className="ds-input w-full"
                  value={newUser.password}
                  onChange={e => setNewUser({...newUser, password: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-fg-2 mb-1.5">Role Assignment</label>
                <div className="relative">
                    <select
                    className="ds-input w-full appearance-none"
                    value={newUser.role}
                    onChange={e => setNewUser({...newUser, role: e.target.value})}
                    >
                    <option value="viewer">Viewer (Read Only)</option>
                    <option value="trader">Trader (Market Access)</option>
                    <option value="admin">Admin (Full Control)</option>
                    <option value="superuser">Superuser (System Owner)</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-fg-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                    </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[var(--divider)]">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createLoading ? 'Creating User...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
